import React from 'react';
import { Chart, Tooltip, Legend, ArcElement } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { SEV_COLORS, SEV_LABELS } from './constants';
import ChartCard from './ChartCard';

Chart.register(Tooltip, Legend, ArcElement);

interface SeverityDonutProps {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

const LEGEND_PAIRS: (keyof typeof SEV_COLORS)[][] = [
  ['Critical', 'High'],
  ['Medium', 'Low'],
  ['Info'],
];

const SeverityDonut: React.FC<SeverityDonutProps> = ({ critical, high, medium, low, info }) => {
  const counts: Record<string, number> = {
    Critical: critical,
    High: high,
    Medium: medium,
    Low: low,
    Info: info,
  };
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const chartData = {
    labels: [...SEV_LABELS],
    datasets: [
      {
        data: [critical, high, medium, low, info],
        backgroundColor: Object.values(SEV_COLORS),
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  };

  const options = {
    cutout: '68%',
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    responsive: true,
    maintainAspectRatio: true,
  } as const;

  return (
    <ChartCard
      title="Severity breakdown"
      sub=""
      noMenu
      extra={
        critical > 0 ? (
          <span style={{ color: SEV_COLORS.Critical, fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
            {critical} <span style={{ fontWeight: 400, fontSize: 11 }}>critical</span>
          </span>
        ) : undefined
      }
    >
      {/* Donut — fixed width, centred */}
      <div className="position-relative" style={{ maxWidth: 200, margin: '8px auto 12px' }}>
        <Doughnut data={chartData} options={options} />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
            lineHeight: 1.2,
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-1px' }}>
            {total.toLocaleString()}
          </div>
          <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>findings</div>
        </div>
      </div>

      {/* 2-column legend — centred */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        {LEGEND_PAIRS.map((pair, rowIdx) => (
          <div key={rowIdx} style={{ display: 'flex', gap: 20 }}>
            {pair.map(label => (
              <span
                key={label}
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: SEV_COLORS[label],
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                {label} ({counts[label]})
              </span>
            ))}
          </div>
        ))}
      </div>
    </ChartCard>
  );
};

export default SeverityDonut;
